import { useContext, useEffect, useRef } from 'react'
import {
  BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS,
  type AmbientPresetId,
} from '~/boggle/ui/ambient/AmbientParticlesConfig'
import {
  getBoggleAmbientPressureBand,
  resolveBoggleRunShellAmbientScene,
  type BoggleAmbientPressureBand,
  type BoggleShellAmbientScene,
} from '~/boggle/ui/chrome/boggleShellAmbientPolicy'
import { BoggleRouteShellAmbientPresetContext } from '~/boggle/ui/chrome/RouteShell'
import {
  hasActiveBoggleScoreFeedback,
  useBoggleScoreFeedback,
} from '~/boggle/ui/run/ScoreFeedback'
import { BOGGLE_GOLD_RUSH_BURST_ID } from '~/boggle/lib/meterBursts'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import { useBoggleUiState } from '~/boggle/ui/state/Store'

type AppliedAmbientScene = {
  preset: AmbientPresetId
  transitionMs: number
}

function normalizeAmbientSceneForApply(scene: BoggleShellAmbientScene): AppliedAmbientScene {
  return {
    preset: scene.preset,
    transitionMs: scene.transitionMs ?? BOGGLE_AMBIENT_DEFAULT_TRANSITION_MS,
  }
}

function areAppliedAmbientScenesEqual(a: AppliedAmbientScene, b: AppliedAmbientScene): boolean {
  return a.preset === b.preset && a.transitionMs === b.transitionMs
}

/**
 * Drives RouteShell ambient particles for an active run: playing phases, end buffer,
 * and summary. Preset selection runs in a store subscription effect so the shell
 * does not re-render on each sim timer tick.
 */
export function useBoggleRunShellAmbientSync(
  store: BoggleStore,
  options: {
    summaryStandardOutcome?: null | 'new-best' | 'tie'
  } = {},
) {
  const setAmbientPreset = useContext(BoggleRouteShellAmbientPresetContext)
  const state = useBoggleUiState(store)
  const scoreFeedback = useBoggleScoreFeedback(store)

  const showEndedSummary =
    state.phase === 'ended' && !hasActiveBoggleScoreFeedback(scoreFeedback)

  const lastAppliedSceneRef = useRef<AppliedAmbientScene | null>(null)
  const lastPressureBandRef = useRef<BoggleAmbientPressureBand | null>(null)
  const pressureBlendDurationLatchRef = useRef<null | number>(null)
  const endBlendDurationLatchRef = useRef<null | number>(null)
  const pendingFocusActiveFrameRef = useRef<null | number>(null)

  useEffect(() => {
    if (!setAmbientPreset) {
      return
    }

    const applyAmbientScene = (scene: BoggleShellAmbientScene) => {
      const normalizedScene = normalizeAmbientSceneForApply(scene)
      const needsInstantFocusBeforeActive =
        scene.preset === 'active' && lastAppliedSceneRef.current === null

      if (needsInstantFocusBeforeActive) {
        const focusScene: AppliedAmbientScene = { preset: 'focus', transitionMs: 0 }
        if (
          lastAppliedSceneRef.current === null ||
          !areAppliedAmbientScenesEqual(focusScene, lastAppliedSceneRef.current)
        ) {
          setAmbientPreset('focus', { transitionMs: 0 })
          lastAppliedSceneRef.current = focusScene
        }

        if (pendingFocusActiveFrameRef.current !== null) {
          cancelAnimationFrame(pendingFocusActiveFrameRef.current)
        }

        pendingFocusActiveFrameRef.current = requestAnimationFrame(() => {
          pendingFocusActiveFrameRef.current = null
          if (
            lastAppliedSceneRef.current !== null &&
            areAppliedAmbientScenesEqual(normalizedScene, lastAppliedSceneRef.current)
          ) {
            return
          }
          setAmbientPreset('active', {
            transitionMs: scene.transitionMs,
          })
          lastAppliedSceneRef.current = normalizedScene
        })
        return
      }

      if (
        lastAppliedSceneRef.current !== null &&
        areAppliedAmbientScenesEqual(normalizedScene, lastAppliedSceneRef.current)
      ) {
        return
      }

      setAmbientPreset(scene.preset, {
        transitionMs: scene.transitionMs,
      })
      lastAppliedSceneRef.current = normalizedScene
    }

    const readPressureBand = () => {
      const timeState = store.getTimeState()
      return getBoggleAmbientPressureBand({
        showEndedSummary,
        summaryStandardOutcome: options.summaryStandardOutcome,
        goldRushActive:
          timeState.meter.burstActive &&
          timeState.meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID,
        intervalDeadlineMs: timeState.intervalDeadlineMs,
        intervalDurationMs: timeState.intervalDurationMs,
        intervalPlayBeginsAtMs: timeState.intervalPlayBeginsAtMs,
        timeMs: timeState.timeMs,
      })
    }

    const syncAmbientFromStore = () => {
      const timeState = store.getTimeState()
      const scene = resolveBoggleRunShellAmbientScene({
        showEndedSummary,
        summaryStandardOutcome: options.summaryStandardOutcome,
        goldRushActive:
          timeState.meter.burstActive &&
          timeState.meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID,
        intervalDeadlineMs: timeState.intervalDeadlineMs,
        intervalDurationMs: timeState.intervalDurationMs,
        intervalPlayBeginsAtMs: timeState.intervalPlayBeginsAtMs,
        timeMs: timeState.timeMs,
        latches: {
          pressureBlendDurationLatchRef,
          endBlendDurationLatchRef,
        },
      })

      lastPressureBandRef.current = readPressureBand()
      applyAmbientScene(scene)
    }

    const syncAmbientFromTime = () => {
      const band = readPressureBand()
      if (band === lastPressureBandRef.current) {
        return
      }

      syncAmbientFromStore()
    }

    syncAmbientFromStore()

    const unsubscribeTime = store.subscribeTime(syncAmbientFromTime)
    const unsubscribeUi = store.subscribeUi(syncAmbientFromStore)

    return () => {
      unsubscribeTime()
      unsubscribeUi()
      if (pendingFocusActiveFrameRef.current !== null) {
        cancelAnimationFrame(pendingFocusActiveFrameRef.current)
      }
      lastAppliedSceneRef.current = null
      lastPressureBandRef.current = null
      pressureBlendDurationLatchRef.current = null
      endBlendDurationLatchRef.current = null
      setAmbientPreset('home')
    }
  }, [options.summaryStandardOutcome, setAmbientPreset, showEndedSummary, store])
}
